import type {
    ActorEvent,
    Field,
    HighLightConditions,
    IndependentAction,
    RecordLikeProtocol,
    RecordOperatorProtocol,
    RichTextContentProtocol,
    TableViewOptions,
    ViewField
} from '@lighthouse/core'
import { conversionHighLightRecord } from '@lighthouse/shared'
import chroma from 'chroma-js'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { ListRecord } from './ListRecord'
import { getDesignStyleMap, getDesignTypeMap } from './utils'

interface ListContentProps {
    eventList?: ActorEvent[]
    checkable?: boolean
    primaryField?: Field
    viewFields: ViewField[]
    isMobile?: boolean
    selectedRecords?: string[]
    highLightRules?: HighLightConditions
    designConfig: TableViewOptions
    recordOperator?: RecordOperatorProtocol['recordOperator']
    onRecordSelect?: (recordIds: string[]) => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

const SCxListContent = styled.div<{ gap: number }>`
    user-select: none;
    display: flex;
    flex-direction: column;
    gap: ${({ gap }) => `${gap}px`};
`

export const ListContent: React.FC<ListContentProps> = ({
    eventList,
    checkable,
    primaryField,
    viewFields,
    isMobile,
    selectedRecords,
    highLightRules = [],
    designConfig,
    recordOperator,
    onRecordSelect,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderButtonTitle
}) => {
    const { recordPoints } = useViewBlockContext()
    const documentStyles = getComputedStyle(document.body)
    const themeColor = documentStyles.getPropertyValue('--color-app-main').trim()
    const { background, isGap } = getDesignTypeMap(designConfig.designType)
    const gap = useMemo(() => {
        const { gap } = getDesignStyleMap(designConfig.designStyle)
        return isGap ? gap : 0
    }, [designConfig.designStyle, isGap])

    const hoverColor = useMemo(() => {
        const hoverColor = chroma(themeColor).alpha(0.06).hex()
        if (!background) {
            return hoverColor
        }
        const backgroundColor = documentStyles.getPropertyValue(background).trim()
        return chroma.mix(hoverColor, backgroundColor).hex()
    }, [background, documentStyles, themeColor])
    const { row: rowStyleConditions } = useMemo(() => conversionHighLightRecord(highLightRules), [highLightRules])

    const recordCells = useMemo(() => {
        return recordPoints.map((point, i) => {
            return (
                <ListRecord
                    key={point.id}
                    id={point.id}
                    eventList={eventList}
                    index={i}
                    viewFields={viewFields}
                    hoverColor={hoverColor}
                    isMobile={isMobile}
                    isLast={recordPoints.length - 1 === i}
                    primaryField={primaryField}
                    rowStyleConditions={rowStyleConditions}
                    checkable={checkable}
                    selectedRecords={selectedRecords}
                    designConfig={designConfig}
                    recordOperator={recordOperator}
                    onRecordSelect={onRecordSelect}
                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                    onRecordClick={onRecordClick}
                    onRenderButtonTitle={onRenderButtonTitle}
                />
            )
        })
    }, [
        recordPoints,
        eventList,
        viewFields,
        hoverColor,
        isMobile,
        primaryField,
        rowStyleConditions,
        checkable,
        selectedRecords,
        designConfig,
        recordOperator,
        onRecordSelect,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderButtonTitle
    ])

    return <SCxListContent gap={gap}>{recordCells}</SCxListContent>
}
